import { employeeBelongsToCompany, unitBelongsToCompany } from "./company";
import { compareEmployeesByStaffGrade } from "./grade";
import { buildOrgLayout, normalizeLayoutConfig, type LayoutRoutedEdge, type LayoutUnitNode } from "./layout";
import { normalizeViewConfig, resolveDisplayScopeRoot, visibleUnitIdsForViewConfig } from "./viewConfig";
import type { CompanyFilter, Employee, OrgEdge, OrgState, OrgUnit, Point } from "../types/org";

export const SLIDE_WIDTH = 1920;
export const SLIDE_HEIGHT = 1080;
export const UNIT_WIDTH = 176;
export const UNIT_HEIGHT = 88;

const CHART_LEFT = 64;
const CHART_RIGHT = 1740;
const CHART_TOP = 198;
const CHART_BOTTOM = 930;
const SAFE_TOP = 150;
const GRADE_TO_LEVEL_SCALE = 12;
const GROUP_GAP_X = 42;
const STAFF_LINE_HEIGHT = 19;
const UNIT_GROUP_TOP_LABEL = 25;
const GROUP_BOTTOM_PAD = 10;
const TITLE_BOUNDARY_CODES = new Set(["B000", "C000", "E000"]);

const BUSINESS_ALIASES: Record<string, string[]> = {
  FIN: ["FIN", "Fin", "AO"],
  HR: ["HR", "AO"],
  Legal: ["Legal"],
  LCD: ["LCD", "PMSG"],
  LDB: ["LDB"],
  NWK: ["NWK"],
};

export interface OrgChartSubtree {
  rootUnit?: OrgUnit;
  visibleUnits: OrgUnit[];
  visibleEmployees: Employee[];
  visibleReportingLines: OrgEdge[];
  visibleDashedReportingLines: OrgEdge[];
}

export interface SlideChartData extends OrgChartSubtree {
  managersByUnitId: Record<string, Employee | undefined>;
  secretariesByUnitId: Record<string, Employee | undefined>;
  directMembersByUnitId: Record<string, Employee[]>;
  sameDepartmentMembersByUnitId: Record<string, Employee[]>;
}

export type PresentationUnit = LayoutUnitNode;

export interface PresentationGradeRow {
  grade: string;
  y: number;
  index: number;
  level: number;
}

export type PresentationEdge = LayoutRoutedEdge;

export interface PresentationLayout {
  title: string;
  period: string;
  canvasWidth: number;
  canvasHeight: number;
  units: PresentationUnit[];
  gradeRows: PresentationGradeRow[];
  edges: PresentationEdge[];
  dashedEdges: PresentationEdge[];
  omittedCount: number;
  warnings: string[];
  chartBounds: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
}

export interface SlideLayoutOptions {
  year: number;
  month: number;
  language: "zh" | "en";
  layoutConfig?: OrgState["layoutConfig"];
  unitsById?: OrgState["units"];
}

export function buildPresentationLayout(state: OrgState): PresentationLayout {
  const chartData = buildSlideChartData(state);
  return computeSlideLayout(chartData, {
    year: state.year,
    month: state.month,
    language: state.language,
    layoutConfig: state.layoutConfig,
    unitsById: state.units,
  });
}

export function buildSlideChartData(state: OrgState): SlideChartData {
  const subtree = getOrgChartSubtree(state, state.selectedUnitId ?? state.focusedUnitId);
  const allEmployees = Object.values(state.employees);
  const managersByUnitId: Record<string, Employee | undefined> = {};
  const secretariesByUnitId: Record<string, Employee | undefined> = {};
  const directMembersByUnitId: Record<string, Employee[]> = {};
  const sameDepartmentMembersByUnitId: Record<string, Employee[]> = {};

  for (const unit of subtree.visibleUnits) {
    managersByUnitId[unit.id] = getManagerForUnit(unit, allEmployees);
    const manager = managersByUnitId[unit.id];
    const secretary = getSecretaryForUnit(unit, allEmployees);
    secretariesByUnitId[unit.id] = secretary;
    const directMembers = getDirectMembersForUnit(unit, subtree.visibleEmployees, subtree.visibleUnits, secretary);
    const sameDepartmentMembers = directMembers.filter((employee) => isSameDepartmentMember(unit, manager, employee));
    sameDepartmentMembersByUnitId[unit.id] = sameDepartmentMembers
      .sort(compareEmployee);
    const sameDepartmentIds = new Set(sameDepartmentMembers.map((employee) => employee.id));
    directMembersByUnitId[unit.id] = directMembers
      .filter((employee) => !sameDepartmentIds.has(employee.id))
      .sort(compareEmployee);
  }

  return {
    ...subtree,
    managersByUnitId,
    secretariesByUnitId,
    directMembersByUnitId,
    sameDepartmentMembersByUnitId,
  };
}

export function computeSlideLayout(chartData: SlideChartData, options: SlideLayoutOptions): PresentationLayout {
  const config = normalizeLayoutConfig(options.layoutConfig);
  if (!chartData.visibleUnits.length) {
    return {
      title: "",
      period: `${options.year}/${String(options.month).padStart(2, "0")}`,
      units: [],
      canvasWidth: SLIDE_WIDTH,
      canvasHeight: SLIDE_HEIGHT,
      gradeRows: [],
      edges: [],
      dashedEdges: [],
      omittedCount: 0,
      warnings: [],
      chartBounds: {
        left: CHART_LEFT,
        right: CHART_RIGHT,
        top: CHART_TOP,
        bottom: CHART_BOTTOM,
      },
    };
  }

  const unitIdsWithVisibleChildren = new Set(chartData.visibleReportingLines.map((edge) => edge.source));
  const inputs = chartData.visibleUnits.map((unit) => ({
    unit,
    manager: chartData.managersByUnitId[unit.id],
    secretary: unit.staffExpanded === false ? undefined : chartData.secretariesByUnitId[unit.id],
    employees: unit.staffExpanded === false ? [] : chartData.directMembersByUnitId[unit.id] ?? [],
    sameDepartmentMembers: unit.staffExpanded === false ? [] : chartData.sameDepartmentMembersByUnitId[unit.id] ?? [],
    sameDepartmentMemberDisplayMode: unitIdsWithVisibleChildren.has(unit.id) ? "external-leader-line" as const : "below-node" as const,
    staffToggleAvailable: Boolean((chartData.directMembersByUnitId[unit.id]?.length ?? 0) + (chartData.sameDepartmentMembersByUnitId[unit.id]?.length ?? 0)),
  }));
  const pipeline = buildOrgLayout({
    units: inputs,
    edges: chartData.visibleReportingLines,
    dashedEdges: chartData.visibleDashedReportingLines,
    language: options.language,
    layoutConfig: config,
    chartBounds: {
      left: CHART_LEFT,
      right: CHART_RIGHT,
      top: CHART_TOP,
      bottom: CHART_BOTTOM,
    },
    preserveManual: true,
    slideScopeId: chartData.rootUnit?.id ?? "",
  });

  return {
    title: getSlideTitle(options.language, pipeline.units, chartData.rootUnit, options.unitsById),
    period: `${options.year}/${String(options.month).padStart(2, "0")}`,
    units: pipeline.units,
    canvasWidth: Math.max(SLIDE_WIDTH, pipeline.chartBounds.right + 64),
    canvasHeight: Math.max(SLIDE_HEIGHT, pipeline.chartBounds.bottom + 64),
    gradeRows: pipeline.gradeRows,
    edges: pipeline.edges,
    dashedEdges: pipeline.dashedEdges,
    omittedCount: 0,
    warnings: pipeline.warnings,
    chartBounds: pipeline.chartBounds,
  };
}

export function getDirectMembersForUnit(unit: OrgUnit, employees: Employee[], subtreeUnits: OrgUnit[], secretary?: Employee) {
  const primaryManagedUnitsByEmployeeNo = new Map<string, OrgUnit[]>();
  for (const managedUnit of subtreeUnits) {
    if (managedUnit.isConcurrent || !managedUnit.managerEmployeeNo?.trim()) continue;
    primaryManagedUnitsByEmployeeNo.set(managedUnit.managerEmployeeNo, [
      ...(primaryManagedUnitsByEmployeeNo.get(managedUnit.managerEmployeeNo) ?? []),
      managedUnit,
    ]);
  }

  return employees.filter((employee) => {
    if (secretary?.id === employee.id) return false;
    if (!employeeDirectlyBelongsToUnit(employee, unit)) return false;
    if (employee.employeeNo === unit.managerEmployeeNo) return false;
    const primaryManagedUnits = primaryManagedUnitsByEmployeeNo.get(employee.employeeNo) ?? [];
    if (primaryManagedUnits.some((managedUnit) => employeeDirectlyBelongsToUnit(employee, managedUnit))) return false;
    return true;
  });
}

export function getOrgChartSubtree(state: OrgState, selectedUnitId?: string): OrgChartSubtree {
  const viewConfig = normalizeViewConfig(state.viewConfig);
  if (viewConfig.viewMode === "custom") {
    const rootUnit = resolveDisplayScopeRoot(state, viewConfig);
    const scopedConfig = rootUnit ? { ...viewConfig, rootUnitId: rootUnit.id } : viewConfig;
    const selectedIds = visibleUnitIdsForViewConfig(state, scopedConfig);
    const visibleUnits = selectedIds
      .map((id) => state.units[id])
      .filter((unit): unit is OrgUnit => Boolean(unit) && !unit.isUnlinked);
    const visibleUnitSet = new Set(visibleUnits.map((unit) => unit.id));
    const visibleEmployees = Object.values(state.employees).filter((employee) => employee.unitId && visibleUnitSet.has(employee.unitId));
    const { visibleReportingLines, visibleDashedReportingLines } = visibleReportingLinesFor(state, visibleUnitSet);

    return {
      rootUnit: rootUnit && visibleUnitSet.has(rootUnit.id) ? rootUnit : visibleUnits[0],
      visibleUnits,
      visibleEmployees,
      visibleReportingLines,
      visibleDashedReportingLines,
    };
  }

  const company = state.companyFilter ?? "Qisda";
  const scopeUnits = Object.values(state.units).filter((unit) => !unit.isUnlinked && unitBelongsToCompany(state, unit, company));
  const resolvedRoot = resolveChartRoot(state, selectedUnitId);
  const rootUnit = resolvedRoot && unitBelongsToCompany(state, resolvedRoot, company) ? resolvedRoot : undefined;
  const selectedIds = rootUnit ? collectUnitSubtree(rootUnit.id, scopeUnits) : fallbackVisibleUnitIds(state);
  const rootGrade = mGradeNumber(rootUnit?.mGrade);
  const scopedIds = rootGrade >= 10
    ? selectedIds.filter((id) => {
      if (id === rootUnit?.id) return true;
      const grade = mGradeNumber(state.units[id]?.mGrade);
      return !grade || grade >= 8;
    })
    : selectedIds;
  const selectedIdSet = new Set(scopedIds);
  const visibleUnits = selectedIds
    .map((id) => state.units[id])
    .filter((unit): unit is OrgUnit => Boolean(unit) && !unit.isUnlinked && selectedIdSet.has(unit.id));
  const visibleUnitSet = new Set(visibleUnits.map((unit) => unit.id));
  const visibleEmployees = Object.values(state.employees).filter((employee) => employee.unitId && visibleUnitSet.has(employee.unitId));
  const { visibleReportingLines, visibleDashedReportingLines } = visibleReportingLinesFor(state, visibleUnitSet);

  return {
    rootUnit: rootUnit && visibleUnitSet.has(rootUnit.id) ? rootUnit : visibleUnits[0],
    visibleUnits,
    visibleEmployees,
    visibleReportingLines,
    visibleDashedReportingLines,
  };
}

function visibleReportingLinesFor(state: OrgState, visibleUnitSet: Set<string>) {
  const visibleEdges = state.edges.filter((edge) => visibleUnitSet.has(edge.source) && visibleUnitSet.has(edge.target));
  return {
    visibleReportingLines: visibleEdges.filter((edge) => edge.style !== "dashed"),
    visibleDashedReportingLines: [
      ...visibleEdges.filter((edge) => edge.style === "dashed"),
      ...state.dashedEdges.filter((edge) => visibleUnitSet.has(edge.source) && visibleUnitSet.has(edge.target)),
    ],
  };
}

export function constrainSlidePosition(point: Point): Point {
  return {
    x: clamp(point.x, CHART_LEFT, 12000),
    y: clamp(point.y, CHART_TOP - 8, 8000),
  };
}

export function nearestGradeRowForY(rows: PresentationGradeRow[], y: number) {
  return [...rows].sort((a, b) => Math.abs(a.y - y) - Math.abs(b.y - y))[0];
}

export function gradeForRow(row?: PresentationGradeRow) {
  return row?.grade.match(/^M\d+$/) ? row.grade : undefined;
}

function collectUnitSubtree(rootUnitId: string, units: OrgUnit[]) {
  const children = childrenMapFor(units);
  const ids: string[] = [];
  const walk = (unitId: string) => {
    ids.push(unitId);
    for (const child of [...(children.get(unitId) ?? [])].sort(compareUnitCode)) {
      walk(child.id);
    }
  };
  walk(rootUnitId);
  return ids;
}

function resolveChartRoot(state: OrgState, selectedUnitId?: string) {
  const selected = selectedUnitId ? state.units[selectedUnitId] : undefined;
  if (!selected) return undefined;
  const grade = mGradeNumber(selected.mGrade);
  if (grade !== 5 && grade !== 6) return selected;

  let current = selected.parentId ? state.units[selected.parentId] : undefined;
  while (current) {
    const currentGrade = mGradeNumber(current.mGrade);
    if (currentGrade === 7 || currentGrade === 8) return current;
    current = current.parentId ? state.units[current.parentId] : undefined;
  }
  return selected;
}

function fallbackVisibleUnitIds(state: OrgState) {
  return filterUnits(state);
}

function getManagerForUnit(unit: OrgUnit, employees: Employee[]) {
  if (!unit.managerEmployeeNo) return undefined;
  return employees.find((employee) => employee.employeeNo === unit.managerEmployeeNo);
}

function getSecretaryForUnit(unit: OrgUnit, employees: Employee[]) {
  const secretaryByNo = unit.secretaryEmployeeNo
    ? employees.find((employee) => employee.employeeNo === unit.secretaryEmployeeNo)
    : undefined;
  if (secretaryByNo && employeeDirectlyBelongsToUnit(secretaryByNo, unit)) return secretaryByNo;

  const secretaryNames = [unit.secretaryNameZh, unit.secretaryNameEn].filter((name): name is string => Boolean(name?.trim()));
  const secretaryByName = secretaryNames.length
    ? employees.find((employee) =>
      secretaryNames.some((name) => sameToken(employee.chineseName, name) || sameToken(employee.englishName, name)),
    )
    : undefined;
  if (secretaryByName && employeeDirectlyBelongsToUnit(secretaryByName, unit)) return secretaryByName;

  if (unit.secretaryEmployeeNo || unit.secretaryNameZh || unit.secretaryNameEn) return undefined;
  return employees.find((employee) => employee.isSecretary && employeeDirectlyBelongsToUnit(employee, unit));
}

function employeeDirectlyBelongsToUnit(employee: Employee, unit: OrgUnit) {
  if (employee.unitId) return employee.unitId === unit.id;
  if (employee.departmentCode && unit.code) return sameToken(employee.departmentCode, unit.code);
  return false;
}

function isSameDepartmentMember(unit: OrgUnit, manager: Employee | undefined, employee: Employee) {
  if (!employee.departmentCode) return false;
  if (employee.employeeNo === unit.managerEmployeeNo || employee.employeeNo === manager?.employeeNo) return false;
  const supervisorDepartmentCodes = [manager?.departmentCode, unit.code].filter((code): code is string => Boolean(code?.trim()));
  return supervisorDepartmentCodes.some((code) => sameToken(employee.departmentCode, code));
}

function filterUnits(state: OrgState) {
  const company = state.companyFilter ?? "Qisda";
  const units = Object.values(state.units).filter((unit) => !unit.isUnlinked && unitBelongsToCompany(state, unit, company));
  const selectedBusiness = state.businessUnitFilter ?? "";

  if (!selectedBusiness) {
    const focused = state.focusedUnitId ? state.units[state.focusedUnitId] : undefined;
    if (focused && !focused.isUnlinked && unitBelongsToCompany(state, focused, company)) return expandMatches(state, units, new Set([focused.id]), company);
    return [];
  }

  const aliases = BUSINESS_ALIASES[selectedBusiness] ?? [selectedBusiness];
  const directMatches = new Set<string>();
  const departmentCodes = new Set<string>();

  for (const unit of units) {
    if (matchesBusiness(unit, aliases)) directMatches.add(unit.id);
  }

  for (const employee of Object.values(state.employees)) {
    if (!employeeBelongsToCompany(state, employee, company)) continue;
    if (aliases.some((alias) => sameToken(employee.buBg, alias))) {
      if (employee.unitId) directMatches.add(employee.unitId);
      if (employee.departmentCode) departmentCodes.add(employee.departmentCode);
    }
  }

  for (const unit of units) {
    if (departmentCodes.has(unit.code)) directMatches.add(unit.id);
  }

  if (!directMatches.size) return [];
  return expandMatches(state, units, directMatches, company);
}

function expandMatches(state: OrgState, units: OrgUnit[], matches: Set<string>, company: CompanyFilter) {
  const allowed = new Set<string>();
  for (const id of matches) {
    addAncestors(id, allowed, state, company);
    addDescendants(id, allowed, units);
  }
  return units.filter((unit) => allowed.has(unit.id)).map((unit) => unit.id);
}

function overviewUnits(state: OrgState, units: OrgUnit[], company: CompanyFilter) {
  const allowed = new Set<string>();
  const roots = units.filter((unit) => !unit.parentId || !unitBelongsToCompany(state, state.units[unit.parentId], company));
  const addOverview = (unitId: string, depth: number) => {
    if (depth > 3) return;
    allowed.add(unitId);
    units.filter((unit) => unit.parentId === unitId).sort(compareUnitCode).forEach((child) => addOverview(child.id, depth + 1));
  };
  roots.sort(compareUnitCode).forEach((root) => addOverview(root.id, 0));
  return units.filter((unit) => allowed.has(unit.id)).map((unit) => unit.id);
}

function gradeRowsFor(units: OrgUnit[]) {
  const grades = [...new Set(units.map((unit) => normalizeMGrade(unit.mGrade)).filter(Boolean))];
  const levelFallbacks = [...new Set(units.filter((unit) => !normalizeMGrade(unit.mGrade)).map((unit) => `L${unit.level}`))];
  const rows = [...grades.sort(compareMGradeDesc), ...levelFallbacks.sort(compareLevelAsc)];
  const rowLabels = rows.length ? rows : ["M8", "M7", "M6", "M5"];
  const step = Math.min(170, Math.max(112, (CHART_BOTTOM - CHART_TOP) / Math.max(1, rowLabels.length - 1)));
  return rowLabels.map((grade, index) => ({
    grade,
    y: CHART_TOP + index * step,
    index,
    level: levelFromGrade(grade, index),
  }));
}

function nearestRowForLevel(rows: PresentationGradeRow[], level: number) {
  return [...rows].sort((a, b) => Math.abs(a.level - level) - Math.abs(b.level - level))[0] ?? rows[0];
}

function edgeFor(edge: OrgEdge, unitMap: Map<string, PresentationUnit>) {
  const source = unitMap.get(edge.source);
  const target = unitMap.get(edge.target);
  if (!source || !target) return undefined;
  const sourceX = source.x + source.width / 2;
  const sourceY = source.y + UNIT_HEIGHT * source.scale;
  const targetX = target.x + target.width / 2;
  const targetY = target.y;
  const midY = Math.max(sourceY + 30, Math.min(targetY - 18, sourceY + (targetY - sourceY) / 2));
  return {
    edge,
    source,
    target,
    path: `M ${sourceX} ${sourceY} V ${midY} H ${targetX} V ${targetY}`,
  };
}

function detectLayoutWarnings(units: PresentationUnit[], edges: PresentationEdge[]) {
  const warnings: string[] = [];
  for (const unit of units) {
    const rect = rectForUnit(unit);
    if (rect.left < CHART_LEFT || rect.right > CHART_RIGHT || rect.top < SAFE_TOP || rect.bottom > CHART_BOTTOM) {
      warnings.push("Some nodes exceed the slide safe area.");
      break;
    }
  }

  for (let index = 0; index < units.length; index += 1) {
    for (let otherIndex = index + 1; otherIndex < units.length; otherIndex += 1) {
      if (rectsOverlap(rectForUnit(units[index]), rectForUnit(units[otherIndex]), 8)) {
        warnings.push("Some node groups overlap. Use manual adjustment or split this subtree into another slide.");
        index = units.length;
        break;
      }
    }
  }

  for (const edge of edges) {
    const segments = edgeSegments(edge);
    const blocked = units.some((unit) => {
      if (unit.unit.id === edge.source.unit.id || unit.unit.id === edge.target.unit.id) return false;
      const rect = rectForUnit(unit, 5);
      return segments.some((segment) => segmentIntersectsRect(segment, rect));
    });
    if (blocked) {
      warnings.push("Some reporting lines cross node groups.");
      break;
    }
  }
  return warnings;
}

function edgeSegments(edge: PresentationEdge) {
  const sourceX = edge.source.x + edge.source.width / 2;
  const sourceY = edge.source.y + UNIT_HEIGHT * edge.source.scale;
  const targetX = edge.target.x + edge.target.width / 2;
  const targetY = edge.target.y;
  const midY = Math.max(sourceY + 30, Math.min(targetY - 18, sourceY + (targetY - sourceY) / 2));
  return [
    { x1: sourceX, y1: sourceY, x2: sourceX, y2: midY },
    { x1: sourceX, y1: midY, x2: targetX, y2: midY },
    { x1: targetX, y1: midY, x2: targetX, y2: targetY },
  ];
}

function segmentIntersectsRect(segment: { x1: number; y1: number; x2: number; y2: number }, rect: ReturnType<typeof rectForUnit>) {
  const minX = Math.min(segment.x1, segment.x2);
  const maxX = Math.max(segment.x1, segment.x2);
  const minY = Math.min(segment.y1, segment.y2);
  const maxY = Math.max(segment.y1, segment.y2);
  return maxX >= rect.left && minX <= rect.right && maxY >= rect.top && minY <= rect.bottom;
}

function rectForUnit(unit: PresentationUnit, inflate = 0) {
  return {
    left: unit.x - inflate,
    top: unit.y - UNIT_GROUP_TOP_LABEL - inflate,
    right: unit.x + unit.groupWidth + inflate,
    bottom: unit.y - UNIT_GROUP_TOP_LABEL + unit.groupHeight + inflate,
  };
}

function rectsOverlap(a: ReturnType<typeof rectForUnit>, b: ReturnType<typeof rectForUnit>, gap = 0) {
  return a.left < b.right + gap && a.right + gap > b.left && a.top < b.bottom + gap && a.bottom + gap > b.top;
}

function childrenMapFor(units: OrgUnit[]) {
  const children = new Map<string, OrgUnit[]>();
  for (const unit of units) {
    if (!unit.parentId) continue;
    children.set(unit.parentId, [...(children.get(unit.parentId) ?? []), unit]);
  }
  return children;
}

function matchesBusiness(unit: OrgUnit, aliases: string[]) {
  return aliases.some((alias) =>
    sameToken(unit.buBg, alias) ||
    includesToken(unit.code, alias) ||
    includesToken(unit.nameZh, alias) ||
    includesToken(unit.nameEn, alias),
  );
}

function addAncestors(unitId: string, allowed: Set<string>, state: OrgState, company: CompanyFilter) {
  let current: OrgUnit | undefined = state.units[unitId];
  while (current && unitBelongsToCompany(state, current, company)) {
    allowed.add(current.id);
    current = current.parentId ? state.units[current.parentId] : undefined;
  }
}

function addDescendants(unitId: string, allowed: Set<string>, units: OrgUnit[]) {
  allowed.add(unitId);
  for (const child of units.filter((unit) => unit.parentId === unitId)) addDescendants(child.id, allowed, units);
}

function normalizeMGrade(value?: string) {
  const match = String(value ?? "").match(/M*\s*(\d+)/i);
  return match ? `M${match[1]}` : "";
}

function mGradeNumber(value?: string) {
  const match = String(value ?? "").match(/M*\s*(\d+)/i);
  return match ? Number(match[1]) : 0;
}

function getSlideTitle(language: "zh" | "en", units: PresentationUnit[], rootUnit?: OrgUnit, unitsById?: OrgState["units"]) {
  const root = rootUnit ?? units[0]?.unit;
  if (!root) return "Organization";
  const chain = titleUnitChain(root, unitsById);
  const names = chain.map((unit) => unitName(unit, language)).filter(Boolean);
  return unique(names).join(" ") || "Organization";
}

function titleUnitChain(root: OrgUnit, unitsById?: OrgState["units"]) {
  if (!unitsById) return [root];
  const chain: OrgUnit[] = [];
  let current: OrgUnit | undefined = root;
  const visited = new Set<string>();
  while (current && !visited.has(current.id)) {
    chain.unshift(current);
    visited.add(current.id);
    current = current.parentId ? unitsById[current.parentId] : undefined;
  }

  let boundaryIndex = -1;
  for (let index = 0; index < chain.length - 1; index += 1) {
    if (isTitleBoundaryUnit(chain[index])) boundaryIndex = index;
  }
  if (boundaryIndex >= 0) return chain.slice(boundaryIndex + 1);
  return chain;
}

function isTitleBoundaryUnit(unit: OrgUnit) {
  const code = unit.code.trim().toUpperCase();
  if (TITLE_BOUNDARY_CODES.has(code)) return true;
  return code === "D000" && isBenQUnit(unit);
}

function isBenQUnit(unit: OrgUnit) {
  return String(unit.sourceSystem ?? "").toLowerCase() === "benq" || unit.id.toLowerCase().includes("_benq_");
}

function unitName(unit: OrgUnit, language: "zh" | "en") {
  return language === "en" ? unit.nameEn || unit.nameZh || unit.code : unit.nameZh || unit.nameEn || unit.code;
}

function compareUnitCode(a: OrgUnit, b: OrgUnit) {
  return a.code.localeCompare(b.code, undefined, { numeric: true }) || a.nameZh.localeCompare(b.nameZh);
}

function compareMGradeDesc(a: string, b: string) {
  return Number(b.slice(1)) - Number(a.slice(1));
}

function compareLevelAsc(a: string, b: string) {
  return Number(a.slice(1)) - Number(b.slice(1));
}

function levelFromGrade(grade: string, fallback: number) {
  if (grade.startsWith("M")) return GRADE_TO_LEVEL_SCALE - Number(grade.slice(1));
  if (grade.startsWith("L")) return Number(grade.slice(1));
  return fallback;
}

function compareEmployee(a: Employee, b: Employee) {
  return compareEmployeesByStaffGrade(a, b);
}

function sameToken(value: unknown, token: string) {
  return String(value ?? "").trim().toLowerCase() === token.trim().toLowerCase();
}

function includesToken(value: unknown, token: string) {
  return String(value ?? "").toLowerCase().includes(token.trim().toLowerCase());
}

function unique(values: string[]) {
  return [...new Set(values)];
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}
